/**
 * =============================================================================
 * REPLY MODERATION PANEL COMPONENT
 * =============================================================================
 *
 * Panel displaying all replies for a question with moderation actions.
 * Integrates into QuestionDetailModal below the question content.
 *
 * GAP-008: Admin Reply & Solution Management UI
 *
 * =============================================================================
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Button, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import { ReplyItem } from './ReplyItem';
import { StaffReplyComposer } from './StaffReplyComposer';
import { AttachImagesTray } from '../Media/AttachImagesTray';
import { useReplies } from '../../hooks/useReplies';
import { useMediaAuthoring } from '../../hooks/useMediaAuthoring';
import { SolutionWriteNotice } from './SolutionWriteNotice';
import type { AdminQuestionListItem } from '@find-my-part/types';
import { tokens } from '../../tokens';
import { EmptyState } from '../common/EmptyState';

/**
 * Question statuses that accept new replies. UI-only allow-list that MIRRORS
 * the backend `ReplyService.createStaffReply` allow-list; it gates the composer
 * for operator UX only — the backend remains authoritative. Kept as a runtime
 * list (not a shared constant) because `AdminQuestionListItem.status` is typed
 * `string`, so compile-time exhaustiveness is not available here.
 */
const REPLY_ACCEPTING_STATUSES: readonly string[] = [
  'OPEN',
  'IN_PROGRESS',
  'NEEDS_MORE_INFO',
  'RESOLVED',
];

/**
 * Props for ReplyModerationPanel component.
 */
export interface ReplyModerationPanelProps {
  /** Question ID to fetch replies for */
  questionId: string;
  /** Current question status — gates the staff reply composer (UX-only) */
  questionStatus: AdminQuestionListItem['status'];
  /** Auth token for API calls */
  token: string;
  /**
   * ext-W2-C2 dirty-close guard wiring (all three optional — the parent
   * detail dialog supplies them so its OWN close routes cannot silently
   * destroy a live rejection draft; M4 "prompts before discard"):
   * reports whether an open reject form carries a non-empty draft.
   */
  onRejectDraftDirtyChange?: (dirty: boolean) => void;
  /**
   * Incrementing nonce: the parent requests permission to close. The panel
   * routes it through the open form (prompt on a live draft) and answers via
   * `onParentCloseResolve`.
   */
  parentCloseRequestNonce?: number;
  /** Verdict on a close request: proceed=true means the parent may close. */
  onParentCloseResolve?: (proceed: boolean) => void;
  /** Optional test ID prefix */
  testId?: string;
}

/**
 * A draft-destroying action parked while the open reject form decides
 * (internal R1, generalized by external round 2 — ext-W2-F1/-C2).
 */
type RejectBlockedIntent =
  | { kind: 'switch'; replyId: string }
  | { kind: 'page'; page: number }
  | { kind: 'parent-close' };

/**
 * Skeleton loading card for reply list.
 */
function ReplySkeleton(): React.ReactElement {
  return (
    <Box
      padding="medium"
      marginBottom="small"
      style={{
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: tokens.dimension.radius.lg,
      }}
    >
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}
      </style>
      {/* Header skeleton */}
      <Flex flexGap="0.5rem" marginBottom="small">
        <Box
          style={{
            width: '80px',
            height: '16px',
            backgroundColor: tokens.color.surface.skeleton,
            borderRadius: tokens.dimension.radius.sm,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <Box
          style={{
            width: '50px',
            height: '16px',
            backgroundColor: tokens.color.surface.skeleton,
            borderRadius: tokens.dimension.radius.sm,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <Box
          style={{
            width: '60px',
            height: '16px',
            backgroundColor: tokens.color.surface.skeleton,
            borderRadius: tokens.dimension.radius.sm,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      </Flex>
      {/* Body skeleton */}
      <Box
        style={{
          width: '100%',
          height: '16px',
          backgroundColor: tokens.color.surface.skeleton,
          borderRadius: tokens.dimension.radius.sm,
          marginBottom: tokens.dimension.space.xsm,
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
      <Box
        style={{
          width: '60%',
          height: '16px',
          backgroundColor: tokens.color.surface.skeleton,
          borderRadius: tokens.dimension.radius.sm,
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
    </Box>
  );
}

/**
 * Panel displaying all replies for a question with moderation actions.
 *
 * Fetches replies on mount using adminApi.replies.list().
 * Supports approve/reject/hide actions and solution toggle.
 *
 * @example
 * ```tsx
 * <ReplyModerationPanel
 *   questionId="q_abc123xyz789"
 *   token={authToken}
 * />
 * ```
 */
export function ReplyModerationPanel({
  questionId,
  questionStatus,
  token,
  onRejectDraftDirtyChange,
  parentCloseRequestNonce,
  onParentCloseResolve,
  testId,
}: ReplyModerationPanelProps): React.ReactElement {
  // PR4b (ADR-022 §10(A)): the replyId of the just-created staff reply — the
  // anchor for the post-create image-attach affordance. Cleared on dismiss
  // and on question/token change (a stale anchor must never cross questions).
  const [attachReplyId, setAttachReplyId] = useState<string | null>(null);
  useEffect(() => {
    setAttachReplyId(null);
  }, [questionId, token]);

  const {
    replies,
    pagination,
    isLoading,
    error,
    pendingActions,
    isCreatingStaffReply,
    isSolutionActionInFlight,
    solutionPendingReplyId,
    solutionWriteIssue,
    page,
    setPage,
    refetch,
    createStaffReply,
    approveReply,
    rejectReply,
    hideReply,
    markSolution,
    unmarkSolution,
    recoverSolutionWrite,
    dismissSolutionWriteIssue,
  } = useReplies({
    questionId,
    questionStatus,
    token,
    onCreated: setAttachReplyId,
  });

  const panelRef = useRef<HTMLDivElement>(null);
  const solutionFocusReplyIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (solutionWriteIssue) {
      solutionFocusReplyIdRef.current = solutionWriteIssue.replyId;
    }
  }, [solutionWriteIssue]);

  const restoreSolutionFocus = useCallback((): HTMLElement | null => {
    const replyId = solutionFocusReplyIdRef.current;
    const panel = panelRef.current;
    if (!replyId || !panel) return panel;
    const trigger = Array.from(
      panel.querySelectorAll<HTMLButtonElement>('[data-solution-reply-id]')
    ).find((button) => button.dataset.solutionReplyId === replyId);
    if (trigger && !trigger.disabled) return trigger;
    return (
      Array.from(panel.querySelectorAll<HTMLElement>('[data-reply-id]')).find(
        (row) => row.dataset.replyId === replyId
      ) ?? panel
    );
  }, []);

  const { uploadReplyImage } = useMediaAuthoring(token);

  // Per-file upload for the attach tray; a successful upload refetches the
  // list so the reply row reflects its new image without a manual refresh.
  const handleAttachUpload = useCallback(
    async (file: File): Promise<{ success: boolean; message?: string }> => {
      if (!attachReplyId) return { success: false, message: 'No reply selected.' };
      const outcome = await uploadReplyImage(attachReplyId, file);
      if (outcome.success) {
        await refetch();
      }
      return outcome;
    },
    [attachReplyId, uploadReplyImage, refetch]
  );

  const canPostStaffReply = REPLY_ACCEPTING_STATUSES.includes(questionStatus);

  // Inline reject expansion (ADR-025 A-Q2 / M4 — replaces the nested
  // RejectionReasonModal): which row's reject form is open (single expansion)
  // plus the in-flight flag for that row's submit.
  const [rejectingReplyId, setRejectingReplyId] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState(false);
  // Internal review R1, generalized by external round 2 (ext-W2-F1 pagination
  // + ext-W2-C2 parent-close): ANY action that would destroy a live draft —
  // opening reject on a DIFFERENT row, changing the page, or closing the
  // parent detail dialog — parks here as an INTENT and is routed through the
  // OPEN form, which alone knows whether a draft exists: no draft → the
  // intent applies immediately; live draft → the same discard prompt, where
  // "Keep editing" denies (and consumes) the intent and only an explicit
  // Discard applies it. M4's "prompts before discard" is unqualified.
  const [pendingRejectIntent, setPendingRejectIntent] = useState<RejectBlockedIntent | null>(null);

  // A stale expansion must never survive a question/token switch.
  useEffect(() => {
    setRejectingReplyId(null);
    setPendingRejectIntent(null);
  }, [questionId, token]);

  /** Apply a guarded intent once the open form (if any) has released it. */
  const applyRejectIntent = useCallback((intent: RejectBlockedIntent): void => {
    switch (intent.kind) {
      case 'switch':
        setRejectingReplyId(intent.replyId);
        break;
      case 'page':
        setRejectingReplyId(null);
        setPage(intent.page);
        break;
      case 'parent-close':
        setRejectingReplyId(null);
        onParentCloseResolve?.(true);
        break;
    }
  }, [setPage, onParentCloseResolve]);

  // ext-W2-F1 belt race (CI-caught): after a refetch drops the open row, the
  // reconciliation belt clears the expansion in a PASSIVE effect — the DOM can
  // already show the row gone while `rejectingReplyId` is still stale. An
  // interaction in that window must not park an intent no rendered form can
  // ever resolve (the belt would then wipe it — one dead click). Liveness is
  // therefore checked AT the interaction points: a stale open-row id is
  // treated as no-open-form, independent of effect timing.
  const openRejectRowIsLive =
    rejectingReplyId !== null && replies.some((r) => r.replyId === rejectingReplyId);

  /**
   * Route a draft-destroying action through the guard: applies immediately
   * when no LIVE form is open; parks as the pending intent otherwise. Inert
   * while a submit is in flight (R1 — a fresh form would render spuriously
   * locked, then vanish when the flight settles).
   */
  const requestGuardedRejectAction = useCallback((intent: RejectBlockedIntent): void => {
    if (isRejecting) return;
    if (!openRejectRowIsLive) {
      applyRejectIntent(intent);
      return;
    }
    setPendingRejectIntent(intent);
  }, [isRejecting, openRejectRowIsLive, applyRejectIntent]);

  const handleRejectClick = useCallback((replyId: string) => {
    if (isRejecting) return;
    if (!openRejectRowIsLive || rejectingReplyId === replyId) {
      setPendingRejectIntent(null);
      setRejectingReplyId(replyId);
      return;
    }
    requestGuardedRejectAction({ kind: 'switch', replyId });
  }, [isRejecting, openRejectRowIsLive, rejectingReplyId, requestGuardedRejectAction]);

  /** The open form's verdict on a parked intent (R1 / ext-W2). */
  const handleResolveRejectIntent = useCallback((proceed: boolean) => {
    if (pendingRejectIntent === null) return;
    if (proceed) {
      applyRejectIntent(pendingRejectIntent);
    } else if (pendingRejectIntent.kind === 'parent-close') {
      // The modal is waiting on a verdict — a denial must reach it so it can
      // drop its pending close.
      onParentCloseResolve?.(false);
    }
    setPendingRejectIntent(null);
  }, [pendingRejectIntent, applyRejectIntent, onParentCloseResolve]);

  // ext-W2-C2: the parent detail dialog asks (via an incrementing nonce)
  // before closing over a dirty draft. Routed like every other intent.
  const lastParentCloseNonce = useRef(parentCloseRequestNonce ?? 0);
  useEffect(() => {
    const nonce = parentCloseRequestNonce ?? 0;
    if (nonce === lastParentCloseNonce.current) return;
    lastParentCloseNonce.current = nonce;
    if (isRejecting) {
      onParentCloseResolve?.(false); // locked mid-flight — never close over it
      return;
    }
    if (!openRejectRowIsLive) {
      onParentCloseResolve?.(true);
      return;
    }
    setPendingRejectIntent({ kind: 'parent-close' });
  }, [parentCloseRequestNonce, isRejecting, openRejectRowIsLive, onParentCloseResolve]);

  // ext-W2-F1 reconciliation belt: if the OPEN row leaves the current list by
  // any route the guards don't cover (a refetch replaced the page contents),
  // the expansion state must not dangle — a dangling id soft-locks reject on
  // the new page (every click parks an intent no rendered form can resolve).
  useEffect(() => {
    if (rejectingReplyId === null) return;
    if (replies.some((r) => r.replyId === rejectingReplyId)) return;
    setRejectingReplyId(null);
    setPendingRejectIntent(null);
  }, [replies, rejectingReplyId]);

  const handleConfirmReject = useCallback(async (replyId: string, reason: string): Promise<void> => {
    setIsRejecting(true);
    try {
      await rejectReply(replyId, reason);
      setRejectingReplyId(null);
      setPendingRejectIntent(null);
    } finally {
      setIsRejecting(false);
    }
  }, [rejectReply]);

  const handleCancelReject = useCallback(() => {
    setRejectingReplyId(null);
    setPendingRejectIntent(null);
  }, []);

  const handleToggleSolution = useCallback(async (replyId: string, isSolution: boolean): Promise<void> => {
    solutionFocusReplyIdRef.current = replyId;
    if (isSolution) {
      await unmarkSolution(replyId);
    } else {
      await markSolution(replyId);
    }
  }, [markSolution, unmarkSolution]);

  const totalReplies = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;
  const showPagination = totalPages > 1;

  return (
    <div ref={panelRef} tabIndex={-1} data-testid={testId}>
      {/* Panel Header */}
      <Flex alignItems="center" justifyContent="space-between" marginBottom="small">
        <FlexItem>
          <Text bold>
            Replies ({isLoading && !replies.length ? '...' : totalReplies})
          </Text>
        </FlexItem>
      </Flex>

      <SolutionWriteNotice
        issue={solutionWriteIssue}
        busy={isSolutionActionInFlight}
        onRecover={recoverSolutionWrite}
        onDismiss={dismissSolutionWriteIssue}
        restoreFocusTo={restoreSolutionFocus}
        testId={testId ? `${testId}-solution-write` : 'solution-write'}
      />

      {/* Staff Reply Composer (status-gated, UX-only — backend authoritative) */}
      {canPostStaffReply ? (
        <>
          <StaffReplyComposer
            onSubmit={createStaffReply}
            isSubmitting={isCreatingStaffReply}
            testId={testId ? `${testId}-composer` : undefined}
          />
          {/* Post-create attach affordance (PR4b — §10(A)): staff images can
              only anchor to a STAFF-authored reply, so the tray appears only
              for the reply just created here. Staff uploads are auto-approved
              server-side (shared tray — fold S3/A5). */}
          {attachReplyId && (
            <AttachImagesTray
              caption="Attach images to your reply"
              onDone={() => setAttachReplyId(null)}
              onUpload={handleAttachUpload}
              testId={testId ? `${testId}-attach` : undefined}
            />
          )}
        </>
      ) : (
        <Box marginBottom="medium">
          <Text
            color="secondary"
            data-testid={testId ? `${testId}-composer-disabled-note` : undefined}
          >
            Staff replies can be posted after this question is open and accepting
            replies.
          </Text>
        </Box>
      )}

      {/* Loading State */}
      {isLoading && replies.length === 0 && (
        <Box aria-busy="true" data-testid={testId ? `${testId}-loading` : undefined}>
          <ReplySkeleton />
          <ReplySkeleton />
          <ReplySkeleton />
        </Box>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Box
          padding="large"
          style={{ textAlign: 'center' }}
          data-testid={testId ? `${testId}-error` : undefined}
        >
          <Text color="secondary">
            Failed to load replies
          </Text>
          <Button
            variant="secondary"
            onClick={refetch}
            data-testid={testId ? `${testId}-retry` : undefined}
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Empty State */}
      {!isLoading && !error && replies.length === 0 && (
        <EmptyState
          variant="centered"
          testId={testId ? `${testId}-empty` : undefined}
          title="No replies yet"
          body="Replies will appear here once posted."
        />
      )}

      {/* Reply List */}
      {!error && replies.length > 0 && (
        <Box
          role="list"
          aria-label="Replies for this question"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {replies.map((reply) => (
            <ReplyItem
              key={reply.replyId}
              reply={reply}
              token={token}
              onApprove={approveReply}
              onReject={handleRejectClick}
              onHide={hideReply}
              onToggleSolution={handleToggleSolution}
              isPending={pendingActions.has(reply.replyId)}
              isSolutionActionPending={solutionPendingReplyId === reply.replyId}
              solutionActionsDisabled={isSolutionActionInFlight}
              contextActionsDisabled={openRejectRowIsLive}
              isRejectFormOpen={rejectingReplyId === reply.replyId}
              isRejectSubmitting={isRejecting && rejectingReplyId === reply.replyId}
              onConfirmReject={handleConfirmReject}
              onCancelReject={handleCancelReject}
              isRejectIntentRequested={
                pendingRejectIntent !== null && rejectingReplyId === reply.replyId
              }
              onResolveRejectIntent={handleResolveRejectIntent}
              onRejectDraftChange={onRejectDraftDirtyChange}
              testId={testId ? `${testId}-reply-${reply.replyId}` : undefined}
            />
          ))}
        </Box>
      )}

      {/* Pagination */}
      {showPagination && (
        <Flex justifyContent="center" flexGap="0.5rem" marginTop="small">
          <FlexItem>
            <Button
              variant="subtle"
              onClick={() => requestGuardedRejectAction({ kind: 'page', page: page - 1 })}
              disabled={page <= 1 || isLoading || isSolutionActionInFlight}
              data-testid={testId ? `${testId}-prev` : undefined}
            >
              Previous
            </Button>
          </FlexItem>
          <FlexItem style={{ display: 'flex', alignItems: 'center' }}>
            <Text color="secondary">
              Page {page} of {totalPages}
            </Text>
          </FlexItem>
          <FlexItem>
            <Button
              variant="subtle"
              onClick={() => requestGuardedRejectAction({ kind: 'page', page: page + 1 })}
              disabled={page >= totalPages || isLoading || isSolutionActionInFlight}
              data-testid={testId ? `${testId}-next` : undefined}
            >
              Next
            </Button>
          </FlexItem>
        </Flex>
      )}
    </div>
  );
}
